"use client"

import React from "react"
// Removed redundant import: import type React from "react"
import { useState, useEffect, useRef, useCallback } from "react"
import { ArrowUp, Brain, CheckCircle, Plus } from "lucide-react"
import { Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { useChatModeStore } from "@/lib/stores/chat-mode-store"
import { MessageRenderer, type RichMessageContent } from "./message-renderer"
import type { MemorySuggestion } from "./inline-memory-card"
import { QuickWinsToast } from "./quick-wins-toast"
import { FirstImpressionsCard } from "./first-impressions-card"
import { getQuickWins, type QuickWin } from "@/src/intelligence/quick-wins"
import type { SectionSlug } from "@/lib/types/sections"
import { createClient } from "@/lib/supabase/client"
import { useBusinessBrain } from "@/lib/hooks/use-business-brain"
import {
  getFieldByIndex,
  generateNextQuestionBlock,
  generateSavedMemoryBlock,
  CORE_FIELDS,
} from "@/lib/constants/memory"
import type { MemorySuggestionForUI } from "@/lib/types/memory-suggestions"
import { FF_ALWAYS_ADVISOR_MODE, FF_ENABLE_STREAMING } from "@/lib/config/feature-flags"
import { createConversation } from "@/lib/utils/api/conversations"
import { saveMessage, updateConversationTitle, confirmConversation } from "@/lib/db/conversations"
import { useRouter, useParams, useSearchParams } from "next/navigation"
import type { AgentMode } from "@/components/ai/agent-mode-selector"
import { revalidateBrainMemories } from "@/components/brain/brain-page-client"
import { useToast } from "@/hooks/use-toast"
import { useThinkingState } from "@/hooks/useThinkingState"
import { getGreeting } from "@/lib/utils/greeting"
import { findOrphanUserMessage } from "@/lib/chat/orphan"
import { OrphanRecoveryBanner } from "./orphan-recovery-banner"
// Import ThinkingVisual component
import { ThinkingVisual } from "@/components/ui/thinking-visual"
// Import useSidebarStore
import { useSidebarStore } from "@/lib/stores/sidebar-store"
import { useChatComposerExpansion } from "@/lib/chat/use-chat-composer-expansion"
import { shouldShowTimestamp } from "@/lib/utils/date-format"
// Remove duplicate import of shouldShowTimestamp
import { detectArtifact } from "@/lib/chat/artifact-detection"
// Import date formatting helpers
import { formatAbsoluteTimestamp, formatHumanizedTimestamp } from "@/lib/utils/date-format"
// Import AssistantThinkingRow component
import { AssistantThinkingRow } from "@/components/intelligence/assistant-thinking-row"
import { CenterHeader } from "@/components/layout/center-header"

const STARTER_PROMPTS_BY_MODE: Record<AgentMode, string[]> = {
  owner: ["Help me clarify my offer", "What should I focus on next?", "Work through my biggest challenge"],
  marketing: ["Write a 30-day content plan", "Help me refine my messaging", "Review my target market"],
  sales: ["Improve my discovery call script", "Create a sales outreach sequence", "Review my pricing strategy"],
  research: ["Summarize this industry trend", "Analyze my competition", "Research market opportunities"],
}

interface Message {
  id: string
  role: "user" | "assistant" | "system"
  content: string | RichMessageContent
  isError?: boolean
  memorySuggestions?: MemorySuggestion[]
  memoriesUsed?: string[]
  timestamp: string
  savedMemories?: Array<{
    id: string
    label: string
    sectionSlug: string
    dashboardSlug: string
  }>
  requestId?: string // Added for potential error correlation
}

// Define MessageType for MessageRenderer props
type MessageType = "user" | "assistant" | "system"

interface CenteredChatProps {
  activeSection: SectionSlug | null
  onSectionChange: (section: SectionSlug | null) => void
  onSignupClick: () => void
  onEditSection: (sectionSlug: SectionSlug, fieldPath?: string) => void
  onOpenNextSection: () => void
  className?: string
  initialQuestion?: string
  rightPanel?: React.ReactNode
  conversationId?: string // Added conversationId prop
  initialMessages?: Message[]
  refreshSidebar: () => void // Added refreshSidebar prop
  selectedModel?: string // Added default value
  resetQuestionTrigger?: () => void
  agentMode?: AgentMode // Set default value
  onMessageChange?: (messages: Message[]) => void
  initialMode?: "profile" | "advisor" // Added initialMode prop
  mode?: "profile" | "advisor" // Added mode prop
  // P1 Milestone B: Add props for session intent initiation
  shouldLoadConversation?: boolean
  chatMessages?: Message[]
  showHomeScreen?: boolean
  // </CHANGE>
}

function validateErrorHandling(data: any): void {
  if (data && data.ok === false) {
    // Invariant: ok:false must have error object with code and requestId
    if (!data.error || typeof data.error !== "object") {
      console.error("[v0] [P0 REGRESSION] ok:false response missing error object:", data)
      // Don't throw in production - log only
      return
    }
    if (!data.error.code) {
      console.error("[v0] [P0 REGRESSION] error object missing code:", data.error)
      // Don't throw in production - log only
      return
    }
    // Warning if requestId missing (should be present but not blocking)
    if (!data.error.requestId && !data.requestId) {
      console.warn("[v0] [P0 REGRESSION] error envelope missing requestId for correlation")
    }
  }
}

function getErrorDisplayMessage(errorCode?: string): string {
  switch (errorCode) {
    case "LLM_CONFIG_ERROR":
      return "The AI service is not properly configured. Please contact support."
    case "LLM_API_ERROR":
      return "LogosIQ had trouble connecting to the AI. Your data is safe; please try again in a moment."
    case "SUPABASE_ERROR":
      return "There was a database connection issue. Please try again."
    default:
      return "Something went wrong. Please try again."
  }
}

const DEBUG_CHAT_DIAGNOSTICS = process.env.NEXT_PUBLIC_VERCEL_ENV !== "production"

// Removed EnhancedErrorDisplay component definition as it's now imported

function ModeSwitcher({
  mode,
  onModeChange,
}: { mode: "profile" | "advisor"; onModeChange: (mode: "profile" | "advisor") => void }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-[var(--brain)] font-medium">Memory {mode === "profile" ? "1 of 5" : "1"}</span>
      <span className="text-muted-foreground">·</span>
      <span className="text-muted-foreground">
        {mode === "profile" ? "What's your business called?" : "Advisor Mode"}
      </span>
    </div>
  )
}

function normalizeMessages(msgs: any[]): Message[] {
  return (Array.isArray(msgs) ? msgs : []).map((m, i) => ({
    id: typeof m?.id === "string" && m.id ? m.id : typeof m?.requestId === "string" ? `req-${m.requestId}` : `idx-${i}`,
    role: m?.role === "user" || m?.role === "assistant" || m?.role === "system" ? m.role : "assistant",
    content: m?.content ?? "",
    timestamp: typeof m?.timestamp === "string" ? m.timestamp : new Date().toISOString(),
    requestId: typeof m?.requestId === "string" ? m.requestId : undefined,
    isError: !!m?.isError,
    memorySuggestions: Array.isArray(m?.memorySuggestions) ? m.memorySuggestions : undefined,
    memoriesUsed: Array.isArray(m?.memoriesUsed) ? m.memoriesUsed : undefined,
    savedMemories: Array.isArray(m?.savedMemories) ? m.savedMemories : undefined,
  }))
}

const supabase = createClient()

export function CenteredChat({
  activeSection,
  onSectionChange,
  onSignupClick,
  onEditSection,
  onOpenNextSection,
  className = "", // Default className to empty string
  initialQuestion,
  rightPanel,
  conversationId: propConversationId, // Use passed conversationId
  initialMessages = [], // Default initialMessages to empty array
  refreshSidebar, // Use passed refreshSidebar
  selectedModel = "gpt-4o", // Changed from "gpt-4" to "gpt-4o"
  resetQuestionTrigger,
  agentMode = "owner", // Set default value
  onMessageChange,
  initialMode = "advisor", // Set default value
  mode: propMode, // Use propMode from props
  shouldLoadConversation = true,
  chatMessages: propChatMessages, // Renamed for clarity
  showHomeScreen = true,
  // </CHANGE>
}: CenteredChatProps) {
  const router = useRouter()
  const params = useParams()
  const searchParams = useSearchParams()
  const messagesContainerRef = useRef<HTMLDivElement>(null) // Added for smooth scrolling
  const { toast } = useToast() // Ensure toast is available

  // Track hover and focus states
  const [isStarterHovering, setIsStarterHovering] = useState(false)
  const [isInputFocused, setIsInputFocused] = useState(false)

  // States: 'idle' | 'active' | 'focus' | 'ready'
  // Preserved for future enablement when animation speed deltas are fixed
  const [uiState, setUiState] = useState<"idle" | "active" | "focus" | "ready">("idle")
  const uiStateTimerRef = useRef<NodeJS.Timeout | null>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // </CHANGE>

  const [userName, setUserName] = useState<string | null>(null)
  const [isLoadingUserName, setIsLoadingUserName] = useState(true)
  const [isFirstVisit, setIsFirstVisit] = useState(false)

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const supabase = createClient() // Use createClient here
        const {
          data: { session },
        } = await supabase.auth.getSession()

        if (session?.user) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("first_name, last_name, full_name")
            .eq("id", session.user.id)
            .single()

          if (profile) {
            const name = profile.first_name || profile.full_name?.split(" ")[0] || "there"
            setUserName(name)
          } else {
            setUserName("there")
          }
        } else {
          setUserName("there")
        }
      } catch (error) {
        console.error("[v0] Error fetching user name:", error)
        setUserName("there")
      } finally {
        setIsLoadingUserName(false)
      }
    }

    fetchUserName()
  }, [])

  // </CHANGE>

  const [isGeneratingIntent, setIsGeneratingIntent] = useState(false)

  const [messages, setMessages] = useState<Message[]>(normalizeMessages(propChatMessages || initialMessages))
  const [input, setInput] = useState(initialQuestion || "")
  const [isLoading, setIsLoading] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [orphanMessage, setOrphanMessage] = useState<{ id: string; content: string } | null>(null)
  const hasRunOrphanCheck = useRef(false)
  // </CHANGE>

  const [conversationTitle, setConversationTitle] = useState<string | null>(null)
  // </CHANGE>

  // The following states were duplicated and are now consolidated.
  // const [isFirstImpressions, setIsFirstImpressions] = useState(false)
  // const [hasShownFirstImpressions, setHasShownFirstImpressions] = useState(false)
  // const [savedImpressions, setSavedImpressions] = useState<Array<{ field: string; value: string; section: string }>>([])
  // const [quickWins, setQuickWins] = useState<QuickWin[]>([])
  // const [highlightMessageId, setHighlightMessageId] = useState<string | null>(null)
  // const [currentFieldIndex, setCurrentFieldIndex] = useState(0)

  // Single source of truth for interaction state
  type ThinkingState = "idle" | "engaged" | "typing" | "thinking"

  // Inputs for state derivation
  const isThinking = isLoading // Assistant generating response
  const safeInput = input ?? ""
  const hasText = safeInput.trim().length > 0
  const isEngaged = isInputFocused || isStarterHovering

  // Derive state with explicit precedence: thinking > typing > engaged > idle
  const interactionState: ThinkingState = isThinking ? "thinking" : hasText ? "typing" : isEngaged ? "engaged" : "idle"

  // Map state to breath mode (1:1 mapping)
  const breathMode: "calm" | "standard" | "focused" | "urgent" =
    interactionState === "thinking"
      ? "urgent"
      : interactionState === "typing"
        ? "focused"
        : interactionState === "engaged"
          ? "standard"
          : "calm"
  // </CHANGE>

  // MVP: Locked to calm breathing via feature flag
  // MVP: Lock to calm breathing; state machine preserved for future re-enablement
  const ENABLE_THINKING_STATES = false
  // </CHANGE>

  useEffect(() => {
    if (!ENABLE_THINKING_STATES) {
      // Feature flag disabled: no state transitions
      return
    }

    // Clear any pending timers
    const clearTimers = () => {
      if (uiStateTimerRef.current) {
        clearTimeout(uiStateTimerRef.current)
        uiStateTimerRef.current = null
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
        typingTimeoutRef.current = null
      }
    }

    // Determine target state based on current signals
    const isUserTyping = isTyping && hasText
    const isInteracting = isInputFocused || isStarterHovering

    if (isUserTyping) {
      // User typing → immediate transition to 'focus'
      clearTimers()
      setUiState("focus")

      // Start typing timeout for focus → active transition
      typingTimeoutRef.current = setTimeout(() => {
        if (!isTyping && !hasText) {
          setUiState("active")
        }
      }, 400) // 400ms after typing stops
    } else if (isInteracting) {
      // Hover or input focus → debounced transition to 'active'
      if (uiState === "idle") {
        clearTimers()
        uiStateTimerRef.current = setTimeout(() => {
          setUiState("active")
        }, 250) // 250ms debounce for idle → active
      } else if (uiState === "focus") {
        // Already focused, drop to active immediately when not typing
        clearTimers()
        setUiState("active")
      }
    } else {
      // No interaction → transition back to idle
      if (uiState === "active" || uiState === "ready") {
        clearTimers()
        uiStateTimerRef.current = setTimeout(() => {
          setUiState("idle")
        }, 900) // 900ms for ready → idle
      } else if (uiState === "focus") {
        clearTimers()
        setUiState("idle")
      }
    }

    // Cleanup function
    return () => {
      clearTimers()
    }
  }, [isTyping, hasText, isInputFocused, isStarterHovering, uiState, ENABLE_THINKING_STATES])

  // Map new uiState to breathMode for ThinkingVisual
  // When feature flag is disabled, always use "calm"
  const newBreathMode: "calm" | "standard" | "focused" | "urgent" = ENABLE_THINKING_STATES
    ? uiState === "focus"
      ? "focused"
      : uiState === "active"
        ? "standard"
        : uiState === "ready"
          ? "standard"
          : "calm"
    : "calm" // MVP: always calm when feature flag is off
  // </CHANGE>

  console.log("[v0] Advisor Home ThinkingVisual Config:", {
    uiState,
    newBreathMode,
    config: {
      active: true,
      breathMode: newBreathMode,
      visualEmphasis: "normal",
      motionComplexity: "ambient",
      version: "1.1", // Added version for consistency with ThinkingVisual props
      contextOverride: "advisorHome",
    },
  })

  console.log("[v0] UI State Machine:", {
    uiState,
    newBreathMode,
    isTyping,
    hasText,
    isInputFocused,
    isStarterHovering,
  })
  // </CHANGE>

  const [brainMemories, setBrainMemories] = useState<any[]>([])
  const [dynamicPrompts, setDynamicPrompts] = useState<string[]>(STARTER_PROMPTS_BY_MODE.owner)
  const [isLoadingMemories, setIsLoadingMemories] = useState(false)
  const [memoryCount, setMemoryCount] = useState(0)
  const [preferredName, setPreferredName] = useState<string | null>(null)

  const [recentConversations, setRecentConversations] = useState<any[]>([])
  const [isLoadingConversations, setIsLoadingConversations] = useState(false)

  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null)
  const [streamingContent, setStreamingContent] = useState<string>("")
  const [isSending, setIsSending] = useState(false) // Added for pending state on button
  const [sendLockActive, setSendLockActive] = useState(false)

  const [showOrphanRecovery, setShowOrphanRecovery] = useState(false)
  const [orphanRecoveryAttempted, setOrphanRecoveryAttempted] = useState(false)
  const [isGeneratingOrphanResponse, setIsGeneratingOrphanResponse] = useState(false)

  const hasInitializedDrawer = useRef(false)

  const { openToolDrawer, closeToolDrawer } = useSidebarStore()

  const abortControllerRef = useRef<AbortController | null>(null)
  const hasSetTitle = useRef(false)

  const effectiveConversationId = propConversationId ?? searchParams.get("conversationId") ?? undefined
  const currentConversationId = useRef<string | undefined>(effectiveConversationId)
  // </CHANGE>

  const pendingConversationCreation = useRef(false)
  const firstSuccessfulResponse = useRef(false)
  const idempotencyKeyRef = useRef<string | null>(null)

  const {
    mode: chatModeStoreMode,
    setMode,
    memoryCount: brainMemoryCount,
    setMemoryCount: setBrainMemoryCount,
    keyDetailsCompleted,
  } = useChatModeStore()
  const scrollRef = useRef<HTMLDivElement>(null) // Note: This ref is declared but not used in the provided code.
  // const textareaRef = useRef<HTMLTextAreaElement>(null) // Moved to state initialization

  const {
    memories: savedMemories,
    memoryCount: brainMemoryCountFromBrain, // Renamed to avoid conflict with chat mode store's memoryCount
    keyDetailsCompleted: brainKeyDetails,
    saveMemory,
    archiveMemory,
    restoreMemory,
    getMemoriesForContext,
    mutateMemories, // Added mutateMemories
  } = useBusinessBrain()

  const thinking = useThinkingState()

  const [currentFieldIndex, setCurrentFieldIndex] = useState(0)
  // </CHANGE>

  // Variables for the debug fallback card
  let memorySuggestionsFromStream: MemorySuggestionForUI[] = []
  const lastAssistantMessage = messages.filter((m) => m.role === "assistant").pop()

  const isHomeState = messages?.length === 0 && showHomeScreen
  const showConversationLoading = !!effectiveConversationId && messages.length === 0 && isLoading

  const currentQuestion = getFieldByIndex(currentFieldIndex)

  useEffect(() => {
    if (effectiveConversationId) {
      const supabase = createClient()
      supabase
        .from("conversations")
        .select("title")
        .eq("id", effectiveConversationId)
        .single()
        .then(({ data }) => {
          if (data?.title) {
            setConversationTitle(data.title)
          }
        })
    }
  }, [effectiveConversationId])

  // Consolidated state initialization for firstImpressions and related items
  const [isFirstImpressions, setIsFirstImpressions] = useState(false)
  const [hasShownFirstImpressions, setHasShownFirstImpressions] = useState(false)
  const [savedImpressions, setSavedImpressions] = useState<Array<{ field: string; value: string; section: string }>>([])
  const [quickWins, setQuickWins] = useState<QuickWin[]>([])
  const [highlightMessageId, setHighlightMessageId] = useState<string | null>(null)
  // </CHANGE>

  const handleAcceptMemory = useCallback(
    async (suggestion: MemorySuggestionForUI, editedValue?: string) => {
      console.log("[v0][memory-proposal] Accepting memory:", {
        id: suggestion.id,
        label: suggestion.label,
        originalValue: suggestion.value,
        editedValue,
      })

      const proposalStartTime = Date.now()

      const currentField = CORE_FIELDS.find(
        (f) => f.fieldId === suggestion.fieldKey || f.label.toLowerCase() === suggestion.label.toLowerCase(),
      )
      const currentIndex = currentField
        ? CORE_FIELDS.findIndex((f) => f.fieldId === currentField.fieldId)
        : currentFieldIndex

      try {
        const valueToSave = editedValue || suggestion.value

        console.log("[v0][memory-proposal] Persisting to DB:", {
          field: suggestion.fieldKey || suggestion.label.toLowerCase().replace(/\s+/g, "_"),
          label: suggestion.label,
          value: valueToSave,
          section: suggestion.section || "identity",
        })

        // Save to legacy memories table
        await saveMemory({
          field: suggestion.fieldKey || suggestion.label.toLowerCase().replace(/\s+/g, "_"),
          label: suggestion.label,
          value: valueToSave,
          section: suggestion.section || "identity",
          source: "memory_proposal",
          confidence: suggestion.confidence,
        })

        // Also create semantic memory with embedding
        try {
          await fetch("/api/semantic-memories", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              memoryType: "context",
              title: suggestion.label,
              content: valueToSave,
              confidence: suggestion.confidence,
              domains: [suggestion.domainSlug || "identity"],
              importance: suggestion.confidence > 0.8 ? "high" : "medium",
              proposalTimestamp: proposalStartTime,
            }),
          })
          console.log("[v0][memory-proposal] ✅ Semantic memory created with embedding")
        } catch (semanticError) {
          console.warn("[v0][memory-proposal] Failed to create semantic memory (non-blocking):", semanticError)
        }

        await revalidateBrainMemories()
        console.log("[v0][memory-proposal] ✅ Memory saved and Brain revalidated, sidebar should update")

        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("memory-count-changed"))
        }

        // Log analytics event
        const approvalLatencyMs = Date.now() - proposalStartTime
        await fetch("/api/analytics/events", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.JSON.stringify({
            event: "memory_approved",
            properties: {
              env: process.env.NEXT_PUBLIC_VERCEL_ENV || "development",
              path: window.location.pathname,
              memoryLabel: suggestion.label,
              approvalLatencyMs,
            },
          }),
        }).catch((e) => console.warn("[analytics] Failed to log memory_approved:", e))
      } catch (error) {
        console.error("[v0][memory-proposal] ❌ Failed to save memory:", error)

        toast({
          title: "Failed to save memory",
          description: "There was an error saving this memory. Please try again.",
          variant: "destructive",
        })

        return
      }

      setMessages((prev) =>
        prev.map((msg) => ({
          ...msg,
          memorySuggestions: msg.memorySuggestions?.filter((s) => s.id !== suggestion.id),
        })),
      )

      console.log("[v0][memory-proposal] ✅ Suggestion removed from UI state")

      if (chatModeStoreMode === "profile" && currentIndex < CORE_FIELDS.length - 1) {
        const nextIndex = currentIndex + 1
        const businessNameMemory = savedMemories.find((m) => m.field === "business_name")
        const businessName = suggestion.fieldKey === "business_name" ? suggestion.value : businessNameMemory?.value

        const savedMessageId = `saved-memory-${currentIndex}-${Date.now()}`
        const savedField = CORE_FIELDS[currentIndex]

        if (savedField) {
          const savedMemoryMarkdown = generateSavedMemoryBlock(savedField, suggestion.value, currentIndex + 1)

          const savedMemoryMessage: Message = {
            id: savedMessageId,
            role: "assistant",
            content: {
              text: savedMemoryMarkdown,
              thinking: [{ id: "saved", label: "Memory saved", status: "complete" as const, duration: 50 }],
            },
            timestamp: new Date().toISOString(),
          }

          setMessages((prev) => [...prev, savedMemoryMessage])

          setHighlightMessageId(savedMessageId)
          setTimeout(() => {
            setHighlightMessageId((current) => (current === savedMessageId ? null : current))
          }, 1100)
        }

        const nextQuestionText = generateNextQuestionBlock(nextIndex, businessName)

        if (nextQuestionText) {
          setTimeout(() => {
            const nextQuestionMessage: Message = {
              id: crypto.randomUUID(),
              role: "assistant",
              content: {
                text: nextQuestionText,
                thinking: [{ id: "next", label: "Preparing next question", status: "complete" as const, duration: 30 }],
              },
              timestamp: new Date().toISOString(),
            }
            setMessages((prev) => [...prev, nextQuestionMessage])
            setCurrentFieldIndex(nextIndex) // Use setCurrentFieldIndex
          }, 400)
        }
      } else if (chatModeStoreMode === "profile" && currentIndex === CORE_FIELDS.length - 1) {
        const savedMessageId = `saved-memory-${currentIndex}-${Date.now()}`
        const savedField = CORE_FIELDS[currentIndex]

        if (savedField) {
          const savedMemoryMarkdown = generateSavedMemoryBlock(savedField, suggestion.value, currentIndex + 1)

          const savedMemoryMessage: Message = {
            id: savedMessageId,
            role: "assistant",
            content: {
              text: savedMemoryMarkdown,
              thinking: [{ id: "saved", label: "Memory saved", status: "complete" as const, duration: 50 }],
            },
            timestamp: new Date().toISOString(),
          }

          setMessages((prev) => [...prev, savedMemoryMessage])

          setHighlightMessageId(savedMessageId)
          setTimeout(() => {
            setHighlightMessageId((current) => (current === savedMessageId ? null : current))
          }, 1100)
        }

        setTimeout(() => {
          const celebrationMessage: Message = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: {
              text: `## Business Brain Initialized!

Excellent! I now have a solid foundation to understand your business:

- [x] **Business Name** - Captured
- [x] **Industry** - Captured
- [x] **Products/Services** - Captured
- [x] **Target Market** - Captured
- [x] **Current Goals** - Captured

### What's Next?

You can now:
1. **Ask me anything** about your business strategy
2. **View your Owner Dashboard** to see your business at a glance
3. **Generate a Strategy** based on your profile

*Your memories are saved and I'll reference them in all our conversations.*`,
              thinking: [{ id: "complete", label: "Profile complete", status: "complete" as const, duration: 100 }],
            },
            timestamp: new Date().toISOString(),
          }
          setMessages((prev) => [...prev, celebrationMessage])
          setMode("advisor") // Switch to advisor mode
        }, 500)
      }

      const newMemoryCount = brainMemoryCountFromBrain + 1
      setQuickWins(getQuickWins(suggestion.section || "identity", suggestion.fieldKey, newMemoryCount))

      if (newMemoryCount === 3 && !hasShownFirstImpressions) {
        setTimeout(() => {
          setIsFirstImpressions(true)
          setHasShownFirstImpressions(true)
        }, 1500)
      }
    },
    [
      saveMemory,
      brainMemoryCountFromBrain,
      hasShownFirstImpressions,
      chatModeStoreMode,
      currentFieldIndex,
      savedMemories,
      setMode,
      toast, // Added toast to dependency array
    ],
  )

  const handleRejectMemory = useCallback(async (suggestionId: string) => {
    console.log("[v0][memory-proposal] Rejecting memory:", suggestionId)

    setMessages((prev) =>
      prev.map((msg) => ({
        ...msg,
        memorySuggestions: msg.memorySuggestions?.filter((s) => s.id !== suggestionId),
      })),
    )

    console.log("[v0][memory-proposal] ✅ Dismissed suggestion removed from UI")
  }, [])

  const handleSkipToAdvisor = useCallback(() => {
    setMode("advisor")
    const advisorMessage: Message = {
      id: crypto.randomUUID(),
      role: "assistant",
      content: {
        text: `## Ask me anything

I'm ready to help with your business questions. Since I don't know much about your business yet, my advice will be more general.

**What would you like to know?**

*You can switch to "Teach Me About Your Business" anytime using the mode selector below.*`,
        thinking: [{ id: "1", label: "Switched to Advisor mode", status: "complete" as const, duration: 10 }],
      },
      timestamp: new Date().toISOString(),
    }
    setMessages([advisorMessage])
    textareaRef.current?.focus()
  }, [setMode])

  const showMemorySavedToast = useCallback(
    (savedMemories: Array<{ label: string }>) => {
      const count = savedMemories.length
      const summary = count === 1 ? savedMemories[0].label : `${count} memories`

      toast({
        title: "Saved to Business Brain",
        description: summary,
        action: (
          <button
            onClick={() => {
              window.location.href = "/brain"
            }}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <Brain className="h-4 w-4" aria-hidden="true" />
            View
          </button>
        ),
        duration: 5000,
      })
    },
    [toast],
  )

  const handleSendMessage = useCallback(
    async (content: string, providedFiles?: File[], isRetry = false) => {
      const messageText = content.trim()

      if (!messageText.trim() || isLoading || isSending || sendLockActive) {
        console.log("[p0][dedupe] Message send blocked - conditions:", {
          hasText: !!messageText.trim(),
          isLoading,
          isSending,
          sendLockActive,
        })
        return
      }

      const requestId = crypto.randomUUID()
      // Declare userId here
      const {
        data: { user },
      } = await supabase.auth.getSession()
      const userId = user?.id
      console.log(
        `[p0][dedupe] [${requestId.slice(-8)}] handleSendMessage starting - userId:`,
        userId,
        "conversationId:",
        currentConversationId.current ? currentConversationId.current.slice(-8) : "none",
      )

      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: "user",
        content: messageText,
        timestamp: new Date().toISOString(),
        requestId: requestId,
      }

      setMessages((prev) => [...prev, userMessage])

      thinking.reset()

      setInput("")

      setSendLockActive(true)
      setIsLoading(true)
      setIsSending(true)

      if (!currentConversationId.current && userId && !pendingConversationCreation.current) {
        pendingConversationCreation.current = true

        if (!idempotencyKeyRef.current) {
          idempotencyKeyRef.current = crypto.randomUUID()
          console.log(
            `[p0][dedupe] [${requestId.slice(-8)}] Generated idempotency key:`,
            idempotencyKeyRef.current.slice(-8),
          )
        }

        console.log(`[p0][dedupe] [${requestId.slice(-8)}] Creating new conversation (P0: prevent data loss)`)

        try {
          const draftConvId = await createConversation(userId, chatModeStoreMode, true, idempotencyKeyRef.current)
          if (draftConvId) {
            console.log(`[p0][dedupe] [${requestId.slice(-8)}] Conversation created:`, draftConvId.slice(-8))
            currentConversationId.current = draftConvId

            const saved = await saveMessage(draftConvId, "user", messageText)
            if (saved) {
              console.log(`[p0][dedupe] [${requestId.slice(-8)}] User message persisted BEFORE streaming starts`)
            } else {
              console.error(`[p0][dedupe] [${requestId.slice(-8)}] FAILED to persist user message`)
            }

            if (!hasSetTitle.current) {
              await updateConversationTitle(draftConvId, messageText)
              hasSetTitle.current = true
            }

            console.log(
              `[p0][dedupe] [${requestId.slice(-8)}] Route push starting - target: /advisor/${draftConvId.slice(-8)}`,
            )

            await router.push(`/advisor/${draftConvId}`)

            await new Promise((resolve) => setTimeout(resolve, 100))

            console.log(`[p0][dedupe] [${requestId.slice(-8)}] Route push complete, conversation now has messages`)

            window.dispatchEvent(new CustomEvent("conversation-created"))
            window.dispatchEvent(new CustomEvent("refresh-conversations"))
          } else {
            console.error(`[p0][dedupe] [${requestId.slice(-8)}] Failed to create conversation`)
          }
        } catch (convError) {
          console.error(`[p0][dedupe] [${requestId.slice(-8)}] Exception creating conversation:`, convError)
        } finally {
          pendingConversationCreation.current = false
          idempotencyKeyRef.current = null
        }
      }

      try {
        const memoriesContext = getMemoriesForContext()
        const streamingEnabled = FF_ENABLE_STREAMING

        console.log(`[v0] [CenteredChat] [${requestId.slice(-8)}] Streaming enabled:`, streamingEnabled)

        const {
          data: { user: currentUser }, // Renamed to avoid conflict with 'userId' if it were declared
        } = await supabase.auth.getSession()
        const sessionId = currentConversationId.current
        const userId = currentUser?.id // userId is now available

        let assistantMessageId: string | undefined = undefined
        let serverRequestId = requestId
        let accumulatedCitations: any[] = []
        let accumulatedThinkingSteps: any[] = []
        let memoryKeys: string[] = [] // Initialize memoryKeys
        memorySuggestionsFromStream = [] // Initialize memorySuggestions

        if (streamingEnabled) {
          console.log(`[v0] [CenteredChat] [${requestId.slice(-8)}] Attempting streaming mode`)

          assistantMessageId = crypto.randomUUID()
          const assistantMessage: Message = {
            id: assistantMessageId,
            role: "assistant",
            content: "",
            timestamp: new Date().toISOString(),
            requestId: requestId,
          }

          setMessages((prev) => [...prev, assistantMessage])
          setStreamingMessageId(assistantMessageId)
          setStreamingContent("")

          abortControllerRef.current = new AbortController()

          console.log(`[v0] [CenteredChat] [${requestId.slice(-8)}] dispatching POST /api/intelligence/chat`, {
            streaming: true,
            model: selectedModel,
            hasSession: !!currentUser?.access_token,
          })

          const response = await fetch("/api/intelligence/chat", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Request-Id": requestId,
              ...(currentUser?.access_token ? { Authorization: `Bearer ${currentUser.access_token}` } : {}),
            },
            body: JSON.stringify({
              message: messageText,
              model: selectedModel,
              streaming: true,
              mode: chatModeStoreMode,
              agentMode: agentMode,
              currentFieldId: currentQuestion?.fieldId,
              currentFieldLabel: currentQuestion?.label,
              context: {
                tenantId: userId || "anonymous",
                userId: userId || "anonymous",
                conversationHistory: messages.slice(-10).map((m) => ({
                  role: m.role,
                  content: typeof m.content === "string" ? m.content : m.content?.text || "",
                })),
                savedMemories: memoriesContext ?? [],
              },
              sessionId,
            }),
            signal: abortControllerRef.current.signal,
          })

          serverRequestId = response.headers.get("X-Request-Id") || requestId
          memoryKeys = (
            response.headers.get("X-Memory-Keys") ? JSON.parse(response.headers.get("X-Memory-Keys")!) : []
          ) as string[]

          const memorySuggestionsHeader = response.headers.get("X-Memory-Suggestions")
          memorySuggestionsFromStream = memorySuggestionsHeader ? JSON.parse(memorySuggestionsHeader) : []

          console.log("[v0][memory-proposal] Pipeline - received from server:", {
            count: memorySuggestionsFromStream.length,
            suggestions: memorySuggestionsFromStream.map((s: any) => ({
              id: s.id,
              label: s.label,
              confidence: s.confidence,
            })),
          })

          const contentType = response.headers.get("Content-Type") || ""
          const isJsonFallback = contentType.includes("application/json")

          console.log(
            `[v0] [CenteredChat] [${requestId.slice(-8)}] Response received - status:`,
            response.status,
            "serverRequestId:",
            serverRequestId,
            "contentType:",
            contentType,
            "isJsonFallback:",
            isJsonFallback,
          )

          const processStream = async () => {
            if (!response.body) {
              console.error("[v0] [CenteredChat] No response body")
              setIsLoading(false)
              return
            }

            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let buffer = ""
            let accumulatedText = ""
            let chunkCount = 0

            console.log(
              `[v0] [CenteredChat] [${requestId.slice(-8)}] Stream reader initialized, starting to read chunks...`,
            )

            try {
              while (true) {
                const { done, value } = await reader.read()

                if (done) {
                  console.log(`[v0] [CenteredChat] [${requestId.slice(-8)}] Stream done signal received`)
                  break
                }

                const chunk = decoder.decode(value, { stream: true })
                buffer += chunk

                const lines = buffer.split("\n")
                buffer = lines.pop() || ""

                for (const line of lines) {
                  if (!line.trim()) continue

                  chunkCount++

                  if (line.startsWith("data:")) {
                    const jsonStr = line.slice(5).trim()

                    if (!jsonStr || jsonStr === "[DONE]") continue

                    try {
                      const parsed = JSON.parse(jsonStr)

                      if (parsed.type === "transparency") {
                        const transparencyEvent = parsed.data

                        window.dispatchEvent(
                          new CustomEvent("transparency-event", {
                            detail: transparencyEvent,
                          }),
                        )
                        console.log(
                          `[v0] [CenteredChat] [${requestId.slice(-8)}] transparency event:`,
                          transparencyEvent.type,
                        )
                      } else if (parsed.type === "memory_suggestions" && parsed.suggestions) {
                        memorySuggestionsFromStream = parsed.suggestions
                        console.log(`[v0][memory-proposal][client] received: ${memorySuggestionsFromStream.length}`)
                        console.log(
                          `[v0][memory-proposal][client] suggestions:`,
                          memorySuggestionsFromStream.map((s: any) => ({
                            id: s.id,
                            label: s.label,
                            confidence: s.confidence,
                          })),
                        )
                      } else if (parsed.type === "text-delta" && parsed.delta) {
                        accumulatedText += parsed.delta
                        console.log(
                          `[v0] [CenteredChat] [${requestId.slice(-8)}] text-delta rendered: +${parsed.delta.length} chars`,
                        )

                        setMessages((prev) =>
                          prev.map((m) => (m.id === assistantMessageId ? { ...m, content: accumulatedText } : m)),
                        )
                      } else if (parsed.type === "citations" && parsed.citations) {
                        accumulatedCitations = parsed.citations
                      } else if (parsed.type === "thinking" && parsed.thinking) {
                        accumulatedThinkingSteps = parsed.thinking
                      } else if (parsed.type === "finish") {
                        console.log(`[v0] [CenteredChat] [${requestId.slice(-8)}] finish event received`)
                      }
                    } catch (parseError) {
                      console.warn(
                        `[v0] [CenteredChat] [${requestId.slice(-8)}] Failed to parse SSE JSON, ignoring line:`,
                        line.slice(0, 50),
                      )
                    }
                  } else if (line.startsWith("0:")) {
                    const content = line.slice(2).trim()

                    accumulatedText += content
                    console.log(
                      `[v0] [CenteredChat] [${requestId.slice(-8)}] legacy format rendered: +${content.length} chars`,
                    )

                    setMessages((prev) =>
                      prev.map((m) => (m.id === assistantMessageId ? { ...m, content: accumulatedText } : m)),
                    )
                  }
                }
              }

              console.log(
                `[v0] [CenteredChat] [${requestId.slice(-8)}] Stream processing complete, total text: ${accumulatedText.length} chars`,
              )

              const finalContent: RichMessageContent = {
                text: accumulatedText,
                citations: accumulatedCitations,
                thinking: accumulatedThinkingSteps,
                memoryKeys: memoryKeys.length > 0 ? memoryKeys : undefined,
                memorySuggestions: memorySuggestionsFromStream.length > 0 ? memorySuggestionsFromStream : undefined, // Attach suggestions here
              }

              console.log("[P0] assistant content keys", Object.keys(finalContent))

              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId
                    ? {
                        ...m,
                        content: finalContent,
                        isStreaming: false,
                      }
                    : m,
                ),
              )

              console.log("[v0][memory-proposal] Pipeline - attached to message:", {
                messageId: assistantMessageId,
                count: memorySuggestionsFromStream.length,
                rendered: memorySuggestionsFromStream.length > 0,
              })

              setStreamingMessageId(null)
              setStreamingContent("")
              setIsLoading(false)

              console.log(
                `[v0] [CenteredChat] [${requestId.slice(-8)}] Stream finalization complete, message persisted`,
              )

              window.dispatchEvent(new CustomEvent("conversation-created"))
              window.dispatchEvent(new CustomEvent("refresh-conversations"))

              if (!firstSuccessfulResponse.current && currentConversationId.current && userId) {
                console.log(
                  `[v0] [CenteredChat] [${requestId.slice(-8)}] First successful response - confirming conversation`,
                )
                try {
                  const confirmed = await confirmConversation(currentConversationId.current)
                  if (confirmed) {
                    firstSuccessfulResponse.current = true
                    console.log(
                      `[v0] [CenteredChat] [${requestId.slice(-8)}] Conversation confirmed - now visible in recents`,
                    )

                    window.dispatchEvent(new CustomEvent("conversation-created"))
                    window.dispatchEvent(new CustomEvent("refresh-conversations"))
                  } else {
                    console.error(`[v0] [CenteredChat] [${requestId.slice(-8)}] confirmConversation returned false`)
                  }
                } catch (confirmError) {
                  console.error(
                    `[v0] [CenteredChat] [${requestId.slice(-8)}] Failed to confirm conversation:`,
                    confirmError,
                  )
                }
              }

              if (currentConversationId.current && userId) {
                await saveMessage(currentConversationId.current, "assistant", accumulatedText)
                console.log(`[v0] [CenteredChat] [${requestId.slice(-8)}] Assistant message saved to conversation`)
              }

              return
            } catch (streamError) {
              console.error(`[v0] [CenteredChat] [${requestId.slice(-8)}] Stream processing error:`, streamError)

              const errorContent: RichMessageContent = {
                text: accumulatedText || "Stream was interrupted. Check browser console for details.",
                error: {
                  code: "STREAM_ERROR",
                  message: streamError instanceof Error ? streamError.message : "Unknown stream error",
                  requestId: serverRequestId,
                },
                citations: accumulatedCitations,
                thinking: accumulatedThinkingSteps,
                memoryKeys: memoryKeys.length > 0 ? memoryKeys : undefined,
                memorySuggestions: memorySuggestionsFromStream.length > 0 ? memorySuggestionsFromStream : undefined, // Include suggestions in error content if any
              }

              console.log("[P0] assistant content keys", Object.keys(errorContent))

              const errorMessage: Message = {
                id: assistantMessageId || crypto.randomUUID(),
                role: "assistant",
                content: errorContent,
                isError: true,
                timestamp: new Date().toISOString(),
                requestId: serverRequestId,
              }

              setMessages((prev) => prev.map((m) => (m.id === assistantMessageId ? errorMessage : m)))

              setStreamingMessageId(null)
              setIsLoading(false)

              return
            }
          }

          if (response.ok) {
            if (isJsonFallback) {
              console.log(`[v0] [CenteredChat] [${requestId.slice(-8)}] JSON fallback detected, parsing as JSON`)

              let jsonData
              try {
                jsonData = await response.json()
              } catch (parseError) {
                console.error(`[v0] [CenteredChat] [${requestId.slice(-8)}] Failed to parse JSON fallback:`, parseError)
                throw new Error("Failed to parse JSON fallback response")
              }

              if (
                jsonData.savedMemories &&
                Array.isArray(jsonData.savedMemories) &&
                jsonData.savedMemories.length > 0
              ) {
                window.dispatchEvent(
                  new CustomEvent("memories-saved", {
                    detail: { savedMemories: jsonData.savedMemories },
                  }),
                )
              }

              const fallbackContent: RichMessageContent = {
                text: jsonData.assistantText || "I'm having trouble connecting right now. Please try again.",
                error: jsonData.error,
                memoryKeys: memoryKeys.length > 0 ? memoryKeys : undefined,
                memorySuggestions: jsonData.memorySuggestions, // Include suggestions if available in fallback
              }

              console.log("[P0] assistant content keys (fallback)", Object.keys(fallbackContent))

              const fallbackMessage: Message = {
                id: assistantMessageId || crypto.randomUUID(),
                role: "assistant",
                content: fallbackContent,
                timestamp: new Date().toISOString(),
                memorySuggestions: jsonData.memorySuggestions, // Ensure suggestions are attached here too for older handling
                requestId: serverRequestId,
              }

              setMessages((prev) => prev.map((m) => (m.id === assistantMessageId ? fallbackMessage : m)))

              setStreamingMessageId(null)
              setStreamingContent("")
              setIsLoading(false)

              if (currentConversationId.current && userId) {
                await saveMessage(currentConversationId.current, "assistant", fallbackContent.text)
                console.log(`[v0] [CenteredChat] [${requestId.slice(-8)}] Fallback message persisted to DB`)
              }

              window.dispatchEvent(new CustomEvent("conversation-created"))
              window.dispatchEvent(new CustomEvent("refresh-conversations"))

              if (!firstSuccessfulResponse.current && currentConversationId.current && userId) {
                console.log(
                  `[v0] [CenteredChat] [${requestId.slice(-8)}] First response (fallback) - confirming conversation`,
                )
                try {
                  const confirmed = await confirmConversation(currentConversationId.current)
                  if (confirmed) {
                    firstSuccessfulResponse.current = true
                    console.log(
                      `[v0] [CenteredChat] [${requestId.slice(-8)}] Conversation confirmed - now visible in recents`,
                    )

                    window.dispatchEvent(new CustomEvent("conversation-created"))
                    window.dispatchEvent(new CustomEvent("refresh-conversations"))
                  }
                } catch (confirmError) {
                  console.error(
                    `[v0] [CenteredChat] [${requestId.slice(-8)}] Failed to confirm conversation:`,
                    confirmError,
                  )
                }
              }

              return
            }

            console.log(`[v0] [CenteredChat] [${requestId.slice(-8)}] Response OK, initializing stream reader`)
            await processStream()
            return
          } else {
            console.log(
              `[v0] [CenteredChat] [${requestId.slice(-8)}] Non-OK response (${response.status}), parsing error JSON`,
            )

            let errorData
            try {
              errorData = await response.json()
              validateErrorHandling(errorData)
            } catch (parseError) {
              console.error(`[v0] [CenteredChat] [${requestId.slice(-8)}] Failed to parse error response:`, parseError)
              errorData = {
                ok: false,
                error: {
                  code: "HTTP_ERROR",
                  message: `Request failed with status ${response.status}`,
                  requestId: serverRequestId,
                },
              }
            }

            const errorContent: RichMessageContent = {
              text: errorData.assistantText || "We couldn't display the assistant response.",
              error: errorData.error,
              requestId: serverRequestId,
              citations: accumulatedCitations,
              thinking: accumulatedThinkingSteps,
              memoryKeys: memoryKeys.length > 0 ? memoryKeys : undefined,
              memorySuggestions: errorData.error?.memorySuggestions || [], // Include suggestions in error content if any
            }

            console.log("[P0] assistant content keys", Object.keys(errorContent))

            const errorMessage: Message = {
              id: crypto.randomUUID(),
              role: "assistant",
              content: errorContent,
              isError: true,
              timestamp: new Date().toISOString(),
              requestId: serverRequestId,
            }

            setMessages((prev) => [...prev, errorMessage])
            setIsLoading(false)

            return
          }
        } else {
          console.log(`[v0] [CenteredChat] [${requestId.slice(-8)}] Using JSON mode (streaming disabled)`)

          abortControllerRef.current = new AbortController()

          console.log(
            `[v0] [CenteredChat] [${requestId.slice(-8)}] Dispatching POST /api/intelligence/chat (JSON mode)`,
            {
              streaming: false,
              model: selectedModel,
              hasSession: !!currentUser?.access_token,
            },
          )

          const jsonResponse = await fetch("/api/intelligence/chat", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Request-Id": requestId,
              ...(currentUser?.access_token ? { Authorization: `Bearer ${currentUser.access_token}` } : {}),
            },
            body: JSON.stringify({
              message: messageText,
              model: selectedModel,
              streaming: false,
              mode: chatModeStoreMode,
              agentMode: agentMode,
              currentFieldId: currentQuestion?.fieldId,
              currentFieldLabel: currentQuestion?.label,
              context: {
                tenantId: userId || "anonymous",
                userId: userId || "anonymous",
                conversationHistory: messages.slice(-10).map((m) => ({
                  role: m.role,
                  content: typeof m.content === "string" ? m.content : m.content?.text || "",
                })),
                savedMemories: memoriesContext ?? [],
              },
              sessionId,
            }),
            signal: abortControllerRef.current.signal,
          })

          const data = await jsonResponse.json()
          serverRequestId = jsonResponse.headers.get("X-Request-Id") || data.error?.requestId || requestId

          console.log(
            `[v0] [CenteredChat] [${requestId.slice(-8)}] JSON response received - ok:`,
            data.ok,
            "serverRequestId:",
            serverRequestId,
          )

          validateErrorHandling(data)

          if (data.ok === false && data.error) {
            console.error(`[v0] [CenteredChat] [${requestId.slice(-8)}] Chat API returned error:`, data.error.code)

            const errorContent: RichMessageContent = {
              text: "We couldn't display the assistant response.",
              error: data.error,
              requestId: serverRequestId,
            }

            console.log("[P0] assistant content keys", Object.keys(errorContent))

            const errorMessage: Message = {
              id: crypto.randomUUID(),
              role: "assistant",
              content: errorContent,
              isError: true,
              timestamp: new Date().toISOString(),
              requestId: serverRequestId,
            }

            setMessages((prev) => [...prev, errorMessage])
            setIsLoading(false)

            return
          }

          if (!data.assistantText || data.assistantText.trim().length === 0) {
            console.error(
              `[v0] [CenteredChat] [${requestId.slice(-8)}] P0 CONTRACT VIOLATION: Empty assistantText in success response`,
            )

            const errorContent: RichMessageContent = {
              text: "We couldn't display the assistant response.",
              error: {
                code: "EMPTY_RESPONSE",
                message: "The AI returned an empty response.",
                requestId: serverRequestId,
              },
            }

            console.log("[P0] assistant content keys", Object.keys(errorContent))

            const errorMessage: Message = {
              id: crypto.randomUUID(),
              role: "assistant",
              content: errorContent,
              isError: true,
              timestamp: new Date().toISOString(),
              requestId: serverRequestId,
            }

            setMessages((prev) => [...prev, errorMessage])
            setIsLoading(false)

            return
          }

          if (data.savedMemories && Array.isArray(data.savedMemories) && data.savedMemories.length > 0) {
            window.dispatchEvent(
              new CustomEvent("memories-saved", {
                detail: { savedMemories: data.savedMemories },
              }),
            )
          }

          const successContent: RichMessageContent = {
            text: data.assistantText,
            memoryKeys: data.memoryKeys ? data.memoryKeys : undefined, // Assuming memoryKeys might be returned here too
            citations: data.citations,
            thinking: data.thinking,
            memorySuggestions: data.memorySuggestions, // Include suggestions from JSON response
          }

          console.log("[P0] assistant content keys", Object.keys(successContent))

          const successMessage: Message = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: successContent,
            timestamp: new Date().toISOString(),
            memorySuggestions: data.memorySuggestions, // Ensure suggestions are attached here too for older handling
            citations: data.citations,
            requestId: serverRequestId,
          }

          setMessages((prev) => [...prev, successMessage])
          setIsLoading(false)

          if (!firstSuccessfulResponse.current && currentConversationId.current && userId) {
            console.log(
              `[v0] [CenteredChat] [${requestId.slice(-8)}] First successful response - confirming conversation`,
            )
            try {
              const confirmed = await confirmConversation(currentConversationId.current)
              if (confirmed) {
                firstSuccessfulResponse.current = true
                console.log(
                  `[v0] [CenteredChat] [${requestId.slice(-8)}] Conversation confirmed - now visible in recents`,
                )

                window.dispatchEvent(new CustomEvent("conversation-created"))
                window.dispatchEvent(new CustomEvent("refresh-conversations"))
              } else {
                console.error(`[v0] [CenteredChat] [${requestId.slice(-8)}] confirmConversation returned false`)
              }
            } catch (confirmError) {
              console.error(
                `[v0] [CenteredChat] [${requestId.slice(-8)}] Failed to confirm conversation:`,
                confirmError,
              )
            }
          }

          if (currentConversationId.current && userId) {
            await saveMessage(currentConversationId.current, "assistant", data.assistantText)
            console.log(`[v0] [CenteredChat] [${requestId.slice(-8)}] Assistant message saved to conversation`)
          }
        }
      } catch (error: any) {
        console.error(`[v0] [CenteredChat] [${requestId.slice(-8)}] Fatal error in handleSubmit:`, error)

        // Declare variables here to fix lint errors
        const accumulatedCitations: any[] = []
        const accumulatedThinkingSteps: any[] = []
        const memoryKeys: string[] = []
        // Ensure memorySuggestionsFromStream is declared or cleared before use in the catch block if it's meant to be empty
        // For this context, assuming it's for the debug fallback card and should be cleared on error.
        memorySuggestionsFromStream = []

        const fatalContent: RichMessageContent = {
          text: "An unexpected error occurred. Please try again.",
          error: {
            code: "FATAL_ERROR",
            message: error instanceof Error ? error.message : "Unknown error",
            requestId,
          },
          citations: accumulatedCitations,
          thinking: accumulatedThinkingSteps,
          memoryKeys: memoryKeys.length > 0 ? memoryKeys : undefined,
          memorySuggestions: memorySuggestionsFromStream.length > 0 ? memorySuggestionsFromStream : undefined, // Include suggestions in error content if any
        }

        console.log("[P0] assistant content keys", Object.keys(fatalContent))

        const fatalMessage: Message = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: fatalContent,
          isError: true,
          timestamp: new Date().toISOString(),
          requestId: requestId,
        }

        setMessages((prev) => [...prev, fatalMessage])
        setIsLoading(false)
        setStreamingMessageId(null)
      } finally {
        setIsSending(false)
        if (isLoading) setIsLoading(false)
        setSendLockActive(false)
      }
    },
    [
      // handleSendMessage dependency array corrected
      isLoading,
      isSending,
      sendLockActive,
      messages,
      supabase,
      currentConversationId,
      pendingConversationCreation,
      idempotencyKeyRef,
      router,
      createConversation,
      saveMessage,
      updateConversationTitle,
      hasSetTitle,
      FF_ENABLE_STREAMING,
      selectedModel,
      chatModeStoreMode,
      agentMode,
      currentQuestion?.fieldId,
      currentQuestion?.label,
      getMemoriesForContext,
      abortControllerRef,
      toast,
      thinking,
      setMessages,
      setInput,
      setIsLoading,
      setIsSending,
      setSendLockActive,
      setStreamingMessageId,
      setStreamingContent,
      firstSuccessfulResponse,
      confirmConversation,
    ],
  )

  const handleSubmit = useCallback(
    async (overrideMessage?: string) => {
      await handleSendMessage(overrideMessage || input)
    },
    [input, handleSendMessage], // Added handleSendMessage to dependency array
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit],
  )

  const handleGenerateOrphanResponse = useCallback(async () => {
    if (!currentConversationId.current) {
      console.error("[v0][orphan] Cannot generate response: no conversation ID")
      return
    }

    const orphan = findOrphanUserMessage(messages)
    if (!orphan) {
      console.error("[v0][orphan] Cannot generate response: no orphan found")
      setShowOrphanRecovery(false)
      return
    }

    setIsGeneratingOrphanResponse(true)
    setOrphanRecoveryAttempted(true)

    console.log("[v0][orphan] Generating response for orphan message:", {
      conversationId: currentConversationId.current.slice(-8),
      messageId: orphan.messageId.slice(-8),
      contentPreview: orphan.content.slice(0, 50),
    })

    try {
      // Call the same streaming endpoint as normal messages
      const response = await fetch("/api/intelligence/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: orphan.content,
          conversationId: currentConversationId.current,
          idempotencyKey: `orphan-recovery:${orphan.messageId}`,
        }),
      })

      if (!response.ok) {
        throw new Error(`Failed to generate response: ${response.status}`)
      }

      // Handle streaming response (reuse existing streaming logic)
      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error("No response body")
      }

      const assistantMessageId = `assistant-orphan-${Date.now()}`
      setStreamingMessageId(assistantMessageId)
      setStreamingContent("")

      setMessages((prev) => [
        ...prev,
        {
          id: assistantMessageId,
          role: "assistant",
          content: "",
          timestamp: new Date().toISOString(),
        },
      ])

      const decoder = new TextDecoder()
      let buffer = ""

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop() || ""

        for (const line of lines) {
          if (!line.trim() || !line.startsWith("data: ")) continue

          const data = line.slice(6)
          if (data === "[DONE]") {
            setStreamingMessageId(null)
            setShowOrphanRecovery(false)
            console.log("[v0][orphan] Response generation complete")
            continue
          }

          try {
            const parsed = JSON.parse(data)
            if (parsed.content) {
              setStreamingContent((prev) => prev + parsed.content)
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId ? { ...msg, content: (msg.content as string) + parsed.content } : msg,
                ),
              )
            }
          } catch (e) {
            console.warn("[v0][orphan] Failed to parse streaming chunk:", e)
          }
        }
      }

      console.log("[v0][orphan] ✅ Response generation complete")
    } catch (error) {
      console.error("[v0][orphan] ❌ Failed to generate orphan response:", error)
      toast({
        title: "Failed to generate response",
        description: "There was an error generating the assistant response. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsGeneratingOrphanResponse(false)
      setStreamingMessageId(null)
    }
  }, [messages, toast])

  const handleDismissOrphanRecovery = useCallback(() => {
    setShowOrphanRecovery(false)
    setOrphanRecoveryAttempted(true)
    console.log("[v0][orphan] Recovery banner dismissed by user")
  }, [])

  const displayMemoryCount = chatModeStoreMode === "profile" ? Math.min(brainKeyDetails + 1, 5) : currentFieldIndex + 1

  const getContextualPlaceholder = useCallback(() => {
    if (chatModeStoreMode === "brain-builder") {
      if (currentFieldIndex < savedMemories.length) {
        const currentField = savedMemories[currentFieldIndex]
        if (
          !currentField.value ||
          currentField.value === "N/A" ||
          currentField.value === "" ||
          currentField.value.trim().length === 0
        ) {
          return currentField.prompt
        }
        return currentField.prompt
      }
      return "Type your thoughts..."
    }
    // Show "What are you working on right now?" for home state, "Type your thoughts..." for active chats
    // P1 Milestone B: Use derived isHomeState
    if (messages?.length === 0 && isHomeState) {
      return "What are you working on right now?"
    }
    if (brainKeyDetails < 5) {
      return "Type your thoughts..."
    }
    return "Ask anything about your business..."
  }, [chatModeStoreMode, brainKeyDetails, currentFieldIndex, savedMemories, messages?.length, isHomeState]) // Added isHomeState

  const businessName = savedMemories?.find((m) => m.field === "business_name")?.value || null

  const focusStateRef = React.useRef<{
    wasFocused: boolean
    selectionStart: number
    selectionEnd: number
  }>({ wasFocused: false, selectionStart: 0, selectionEnd: 0 })

  const lineHeight = 24 // Base line height for 16px text with leading-6
  const { isMultiLine, computedHeight, checkExpansion } = useChatComposerExpansion({
    textareaRef,
    value: input,
  })
  // </CHANGE>

  console.log("[ChatInputRender] Hook state", {
    isMultiLine,
    inputLength: input.length,
  })

  const belowThresholdCountRef = React.useRef(0) // Tracks consecutive measurements below collapse threshold

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setInput(newValue)

    // Manual trigger still available if needed
    checkExpansion()
  }

  React.useLayoutEffect(() => {
    if (focusStateRef.current.wasFocused && textareaRef.current) {
      // Restore focus and cursor position synchronously before paint
      textareaRef.current.focus({ preventScroll: true })
      textareaRef.current.setSelectionRange(focusStateRef.current.selectionStart, focusStateRef.current.selectionEnd)
    }
  }, [isMultiLine])

  const hasProcessedInitialQuestion = useRef(false)
  useEffect(() => {
    if (initialQuestion && !hasProcessedInitialQuestion.current && messages.length === 0) {
      hasProcessedInitialQuestion.current = true
      setInput(initialQuestion)
      setTimeout(() => {
        textareaRef.current?.focus()
      }, 100)
    }
  }, [initialQuestion, messages.length])

  // Function to handle session intent initiation
  const handleSessionIntent = useCallback(
    async (intentType: "continuity" | "goal" | "decision" | "commitment" | "choice") => {
      setIsGeneratingIntent(true)
      console.log(`[SessionIntent] Handling intent: ${intentType}`)

      try {
        const {
          data: { user },
        } = await supabase.auth.getSession()
        const userId = user?.id // userId declared here

        if (!userId) {
          toast({
            title: "Authentication required",
            description: "Please log in to initiate a session.",
            variant: "destructive",
          })
          setIsGeneratingIntent(false)
          return
        }

        const currentConversationIdVal = currentConversationId.current // Use a local variable

        let newConversationId: string | null = null

        if (!currentConversationIdVal) {
          console.log(`[SessionIntent] No current conversation, creating new one for intent: ${intentType}`)
          const idempotencyKey = crypto.randomUUID()
          newConversationId = await createConversation(userId, "owner", true, idempotencyKey) // Use 'owner' mode for initial intent capture
          if (!newConversationId) throw new Error("Failed to create conversation")
          currentConversationId.current = newConversationId
          console.log(`[SessionIntent] New conversation created: ${newConversationId}`)
          await router.push(`/advisor/${newConversationId}`) // Navigate immediately
          await new Promise((resolve) => setTimeout(resolve, 100)) // Short delay for navigation
        } else {
          console.log(`[SessionIntent] Resuming existing conversation: ${currentConversationIdVal}`)
          newConversationId = currentConversationIdVal // Use the existing ID
        }

        const promptSuffix = {
          continuity: "Where are we at? Provide a summary of the current state and recent activities.",
          goal: "What is the primary goal we should focus on right now? Help me identify and clarify it.",
          decision:
            "What is the most critical decision we need to make next? Guide me through the decision-making process.",
          commitment: "What is the primary goal we should focus on right now? Help me identify and clarify it.", // Mapped from "Focus on my goal"
          choice:
            "What is the most critical decision we need to make next? Guide me through the decision-making process.", // Mapped from "Decide next move"
        }[intentType]

        const messageContent = `I need help with: ${promptSuffix}`
        const systemMessage: Message = {
          id: crypto.randomUUID(),
          role: "system",
          content: `User is initiating a new session with intent: ${intentType}. Provide contextually relevant guidance based on the following request: ${messageContent}`,
          timestamp: new Date().toISOString(),
        }

        // Add the system message to the beginning of messages for context
        setMessages([systemMessage])

        // Send the prompt to the chat API
        await handleSendMessage(messageContent)

        // Update the conversation title based on intent (optional, can be done after first response)
        if (newConversationId) {
          await updateConversationTitle(
            newConversationId,
            `Session Intent: ${intentType.charAt(0).toUpperCase() + intentType.slice(1)}`,
          )
        }

        setConversationTitle(`Session Intent: ${intentType.charAt(0).toUpperCase() + intentType.slice(1)}`)
        window.dispatchEvent(new CustomEvent("conversation-created"))
        window.dispatchEvent(new CustomEvent("refresh-conversations"))
      } catch (error) {
        console.error(`[SessionIntent] Error handling intent ${intentType}:`, error)
        toast({
          title: "Failed to start session",
          description: `Could not initiate session for "${intentType}". Please try again.`,
          variant: "destructive",
        })
      } finally {
        setIsGeneratingIntent(false)
      }
    },
    [
      handleSendMessage,
      router,
      toast,
      setMessages,
      setConversationTitle,
      createConversation,
      updateConversationTitle,
      currentConversationId,
      setIsLoading,
      pendingConversationCreation,
      idempotencyKeyRef,
    ],
  )

  useEffect(() => {
    console.log("[v0] SESSION_INTENT_BUTTONS_RENDERED - Stage B deployment verification")
  }, [])

  // P1 Milestone B: Use derived isHomeState and adjust logic
  // const isHomeState = messages?.length === 0 && showHomeScreen
  // </CHANGE>
  const currentPrompts = agentMode === "owner" ? dynamicPrompts : STARTER_PROMPTS_BY_MODE[agentMode]
  const showSessionIntentButtons = isHomeState && !isGeneratingIntent && !isLoadingConversations

  const canResume =
    !isLoadingConversations &&
    Array.isArray(recentConversations) &&
    recentConversations.length > 0 &&
    recentConversations[0]?.id

  return (
    // P1 Milestone B: Adjust container class for full height and width
    <div className={cn("flex flex-col h-screen w-full relative", className)}>
      {/* </CHANGE> */}
      {showOrphanRecovery && !isLoading && (
        <OrphanRecoveryBanner
          onGenerateResponse={handleGenerateOrphanResponse}
          onDismiss={handleDismissOrphanRecovery}
          isGenerating={isGeneratingOrphanResponse}
        />
      )}

      {showConversationLoading && (
        <div className="flex items-center justify-center h-full w-full">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading conversation...</p>
          </div>
        </div>
      )}

      {isHomeState ? (
        /* HOME STATE: Centered content with input and prompts */
        <div className="flex-1 flex items-start justify-center px-4 pt-[26vh]">
          <div className="w-full max-w-[870px] space-y-6">
            {!isLoadingUserName && userName && (
              <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-4 mb-2">
                  <div className="scale-[1.01]">
                    {/* TODO: ThinkingVisual speed-delta issue
                     * Root symptom: CSS keyframe duration updates not producing visible speed deltas
                     * Likely fix: force restart by changing animation-name token per state change,
                     * or switch to Framer Motion with duration bound to state and remount on change
                     * Re-enable ENABLE_THINKING_STATES flag after animation restart mechanism is fixed
                     */}
                    <ThinkingVisual
                      key={`thinking-visual-${uiState}`}
                      config={{
                        active: true,
                        breathMode: newBreathMode,
                        visualEmphasis: "normal",
                        motionComplexity: "ambient",
                        version: "1.1",
                        contextOverride: "advisorHome", // Ensure contextOverride is set if needed
                      }}
                      context="inline"
                      className="mr-3"
                    />
                  </div>
                  <h1 className="text-[2.25rem] font-display font-normal text-slate-300">
                    {getGreeting(userName, false)}
                  </h1>
                </div>
              </div>
            )}

            <div className="relative">
              <div className="w-full max-w-[950px] mx-auto">
                <div
                  className={cn(
                    "relative transition-all duration-200",
                    isMultiLine ? "rounded-[2rem]" : "rounded-full",
                    "bg-slate-900",
                    // Focus state: border + ring only (no fill brightening)
                    "border border-white/10",
                    isInputFocused ? "border-white/20 ring-1 ring-white/10" : "border-white/10",
                    "px-4",
                    isMultiLine ? "py-2.5" : "py-2.5",
                  )}
                  ref={(el) => {
                    // Regression logging for input dimensions
                    if (el && typeof window !== "undefined") {
                      const styles = window.getComputedStyle(el)
                      console.log("[v0] HOME_INPUT_COMPUTED_STYLES", {
                        backgroundColor: styles.backgroundColor,
                        paddingLeft: styles.paddingLeft,
                        paddingRight: styles.paddingRight,
                        paddingTop: styles.paddingTop,
                        paddingBottom: styles.paddingBottom,
                        borderColor: styles.borderColor,
                        focused: isInputFocused,
                      })
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    {/* Left controls: fixed width */}
                    <div className="shrink-0">
                      <button
                        type="button"
                        onClick={() => console.log("Attach clicked")} // Placeholder for attachment logic
                        className="h-10 w-10 rounded-full bg-slate-700/50 flex items-center justify-center hover:bg-slate-700/70 transition-colors"
                        aria-label="Attach file"
                        ref={(el) => {
                          // Log endcap positioning for regression tracking
                          if (el && typeof window !== "undefined") {
                            const rect = el.getBoundingClientRect()
                            const parent = el.parentElement?.parentElement?.parentElement?.getBoundingClientRect()
                            if (parent) {
                              console.log("[v0] HOME_LEFT_ENDCAP_POSITIONING", {
                                buttonWidth: rect.width,
                                buttonHeight: rect.height,
                                leftMargin: rect.left - parent.left,
                                topMargin: rect.top - parent.top,
                                opticalParity: `${rect.left - parent.left}px horizontal ≈ ${rect.top - parent.top}px vertical`,
                              })
                            }
                          }
                        }}
                      >
                        <Plus className="h-5 w-5 text-slate-300" />
                      </button>
                    </div>

                    {/* Center: Textarea wrapper (flex: 1; min-width: 0) */}
                    <div className="flex-1 min-w-0 flex items-center">
                      <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={handleTextareaChange}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setIsInputFocused(false)}
                        placeholder={getContextualPlaceholder()}
                        disabled={isLoading || isSending}
                        rows={1}
                        className={cn(
                          "w-full resize-none bg-transparent text-[18px] leading-[28px] text-white placeholder-slate-400",
                          "border-0 outline-none focus:ring-0",
                          "whitespace-pre-wrap",
                          "[overflow-wrap:anywhere]",
                          "[word-break:break-word]",
                        )}
                        style={{
                          minHeight: "24px",
                          padding: "0",
                          height: computedHeight,
                          maxHeight: "200px",
                          overflow: computedHeight === "200px" ? "auto" : "hidden",
                        }}
                      />
                    </div>

                    {/* Right controls: fixed width */}
                    <div className="shrink-0">
                      <button
                        type="button"
                        onClick={async () => await handleSubmit()}
                        disabled={!input.trim() || isLoading || isSending}
                        className={cn(
                          "h-10 w-10 rounded-full",
                          "flex items-center justify-center transition-all duration-200",
                          input.trim() && !isLoading && !isSending
                            ? "bg-[var(--brain)] hover:bg-[var(--brain-hover)] cursor-pointer"
                            : "bg-[var(--brain)]/30 cursor-not-allowed opacity-60",
                        )}
                        aria-label="Send message"
                        ref={(el) => {
                          // Log endcap positioning for regression tracking
                          if (el && typeof window !== "undefined") {
                            const rect = el.getBoundingClientRect()
                            const parent = el.parentElement?.parentElement?.parentElement?.getBoundingClientRect()
                            if (parent) {
                              console.log("[v0] HOME_RIGHT_ENDCAP_POSITIONING", {
                                buttonWidth: rect.width,
                                buttonHeight: rect.height,
                                rightMargin: parent.right - rect.right,
                                topMargin: rect.top - parent.top,
                                opticalParity: `${parent.right - rect.right}px horizontal ≈ ${rect.top - parent.top}px vertical`,
                              })
                            }
                          }
                        }}
                      >
                        {isLoading || isSending ? (
                          <Loader2 className="h-5 w-5 text-white animate-spin" />
                        ) : (
                          <ArrowUp className="h-5 w-5 text-white" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                {/* </CHANGE> */}
              </div>
            </div>

            {showSessionIntentButtons && (
              <div
                className="flex flex-col sm:flex-row sm:flex-nowrap gap-2 mt-6 max-w-[620px] mx-auto"
                onMouseEnter={() => setIsStarterHovering(true)}
                onMouseLeave={() => setIsStarterHovering(false)}
              >
                <button
                  onClick={() => handleSessionIntent("continuity")}
                  disabled={isGeneratingIntent}
                  className="flex-1 min-w-0 px-2 py-1.5 rounded-full border border-white/10 bg-transparent text-[var(--text-secondary)]/70 hover:bg-[var(--brain)]/8 hover:border-white/20 hover:text-[var(--text-secondary)] transition-all duration-200 text-[13px] leading-normal font-normal whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  ref={(el) => {
                    if (el && typeof window !== "undefined") {
                      const styles = window.getComputedStyle(el)
                      console.log("[v0] STARTER_BUTTON_PADDING", {
                        paddingLeft: styles.paddingLeft,
                        paddingRight: styles.paddingRight,
                        border: styles.borderColor,
                      })
                    }
                  }}
                >
                  {isGeneratingIntent ? "Generating..." : "Where are we at?"}
                </button>
                <button
                  onClick={() => handleSessionIntent("commitment")}
                  disabled={isGeneratingIntent}
                  className="flex-1 min-w-0 px-2 py-1.5 rounded-full border border-white/10 bg-transparent text-[var(--text-secondary)]/70 hover:bg-[var(--brain)]/8 hover:border-white/20 hover:text-[var(--text-secondary)] transition-all duration-200 text-[13px] leading-normal font-normal whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Focus on my goal
                </button>
                <button
                  onClick={() => handleSessionIntent("choice")}
                  disabled={isGeneratingIntent}
                  className="flex-1 min-w-0 px-2 py-1.5 rounded-full border border-white/10 bg-transparent text-[var(--text-secondary)]/70 hover:bg-[var(--brain)]/8 hover:border-white/20 hover:text-[var(--text-secondary)] transition-all duration-200 text-[13px] leading-normal font-normal whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Decide next move
                </button>
              </div>
            )}
            {/* </CHANGE> */}
            {/* </CHANGE> */}

            {chatModeStoreMode === "profile" && !FF_ALWAYS_ADVISOR_MODE && (
              <button
                onClick={handleSkipToAdvisor}
                className="block mx-auto text-xs text-muted-foreground hover:text-foreground transition-colors mt-4"
              >
                Skip profile and ask a question instead
              </button>
            )}
          </div>
        </div>
      ) : (
        /* ACTIVE CHAT STATE: Bottom-aligned input with messages above */
        <div className="flex-1 flex flex-col min-h-0">
          {effectiveConversationId && messages.length > 0 && (
            <div className="shrink-0 px-6 md:px-10 lg:px-12">
              <div className="max-w-[870px] mx-auto">
                <CenterHeader
                  title={conversationTitle || "Advisor"}
                  action={{
                    label: "Business Brain →",
                    href: "/brain",
                  }}
                />
              </div>
            </div>
          )}

          {/* Scrollable messages area */}
          <div
            ref={messagesContainerRef}
            className="flex-1 min-h-0 overflow-y-auto px-6 md:px-10 lg:px-12 py-6"
            style={{ scrollBehavior: "smooth" }}
          >
            <div className="max-w-[870px] mx-auto">
              {/* HOME STATE (rendered only if messages are empty and showHomeScreen is true) */}
              {messages.length === 0 && isHomeState && (
                <div className="flex-1 flex items-center justify-center h-full">
                  <div className="w-full max-w-[870px] space-y-6 text-center">
                    {!isLoadingUserName && userName && (
                      <div className="mb-12">
                        <div className="flex items-center justify-center gap-4 mb-2">
                          <div className="scale-[1.01]">
                            <ThinkingVisual
                              key={`thinking-visual-${uiState}`}
                              config={{
                                active: true,
                                breathMode: newBreathMode,
                                visualEmphasis: "normal",
                                motionComplexity: "ambient",
                                version: "1.1",
                                contextOverride: "advisorHome", // Ensure contextOverride is set if needed
                              }}
                              context="inline"
                              className="mr-3"
                            />
                          </div>
                          <h1 className="text-[2.75rem] font-display font-normal text-slate-300">
                            {getGreeting(userName, false)}
                          </h1>
                        </div>
                      </div>
                    )}

                    <div className="relative">
                      <div className="w-full max-w-[950px] mx-auto">
                        <div
                          className={cn(
                            "relative transition-all duration-200",
                            isMultiLine ? "rounded-[2rem]" : "rounded-full",
                            "bg-slate-800",
                            "px-5",
                            isMultiLine ? "py-3.5" : "py-3.5",
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div className="shrink-0">
                              <button
                                type="button"
                                onClick={() => console.log("Attach clicked")}
                                className="h-10 w-10 rounded-full bg-slate-700/50 flex items-center justify-center hover:bg-slate-700/70 transition-colors"
                                aria-label="Attach file"
                              >
                                <Plus className="h-5 w-5 text-slate-300" />
                              </button>
                            </div>

                            <div className="flex-1 min-w-0">
                              <textarea
                                ref={textareaRef}
                                value={input}
                                onChange={handleTextareaChange}
                                onKeyDown={handleKeyDown}
                                onFocus={() => setIsInputFocused(true)}
                                onBlur={() => setIsInputFocused(false)}
                                placeholder={getContextualPlaceholder()}
                                rows={1}
                                className={cn(
                                  "w-full resize-none bg-transparent text-[18px] leading-[28px] text-white placeholder-slate-400",
                                  // </CHANGE>
                                  "border-0 outline-none focus:ring-0",
                                  "whitespace-pre-wrap",
                                  "[overflow-wrap:anywhere]",
                                  "[word-break:break-word]",
                                )}
                                style={{
                                  minHeight: "24px",
                                  padding: "0",
                                  height: computedHeight,
                                  maxHeight: "200px",
                                  overflow: computedHeight === "200px" ? "auto" : "hidden",
                                }}
                              />
                            </div>

                            <div className="shrink-0">
                              <button
                                type="button"
                                onClick={async () => await handleSubmit()}
                                disabled={!input.trim() || isLoading || isSending}
                                className={cn(
                                  "h-10 w-10 rounded-full",
                                  "flex items-center justify-center transition-all duration-200",
                                  input.trim() && !isLoading && !isSending
                                    ? "bg-[var(--brain)] hover:bg-[var(--brain-hover)] cursor-pointer"
                                    : "bg-[var(--brain)]/30 cursor-not-allowed opacity-60",
                                )}
                                aria-label="Send message"
                              >
                                {isLoading || isSending ? (
                                  <Loader2 className="h-5 w-5 text-white animate-spin" />
                                ) : (
                                  <ArrowUp className="h-5 w-5 text-white" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {showSessionIntentButtons && (
                      <div
                        className="flex flex-col sm:flex-row sm:flex-nowrap gap-2 mt-8 max-w-[620px] mx-auto"
                        onMouseEnter={() => setIsStarterHovering(true)}
                        onMouseLeave={() => setIsStarterHovering(false)}
                      >
                        <button
                          onClick={() => handleSessionIntent("continuity")}
                          disabled={isGeneratingIntent}
                          className="flex-1 min-w-0 px-2 py-2.5 rounded-full border border-[var(--brain)]/30 bg-transparent text-[var(--text-secondary)]/80 hover:bg-[var(--brain)]/10 hover:border-[var(--brain)]/35 hover:text-[var(--text-secondary)] transition-all duration-200 text-[14px] leading-normal font-normal whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isGeneratingIntent ? "Generating..." : "Where are we at?"}
                        </button>
                        <button
                          onClick={() => handleSessionIntent("goal")}
                          disabled={isGeneratingIntent}
                          className="flex-1 min-w-0 px-2 py-2.5 rounded-full border border-[var(--brain)]/30 bg-transparent text-[var(--text-secondary)]/80 hover:bg-[var(--brain)]/10 hover:border-[var(--brain)]/35 hover:text-[var(--text-secondary)] transition-all duration-200 text-[14px] leading-normal font-normal whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Focus on my goal
                        </button>
                        <button
                          onClick={() => handleSessionIntent("decision")}
                          disabled={isGeneratingIntent}
                          className="flex-1 min-w-0 px-2 py-2.5 rounded-full border border-[var(--brain)]/30 bg-transparent text-[var(--text-secondary)]/80 hover:bg-[var(--brain)]/10 hover:border-[var(--brain)]/35 hover:text-[var(--text-secondary)] transition-all duration-200 text-[14px] leading-normal font-normal whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Decide next move
                        </button>
                      </div>
                    )}

                    {chatModeStoreMode === "profile" && !FF_ALWAYS_ADVISOR_MODE && (
                      <button
                        onClick={handleSkipToAdvisor}
                        className="block mx-auto text-xs text-muted-foreground hover:text-foreground transition-colors mt-4"
                      >
                        Skip profile and ask a question instead
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ACTIVE CHAT STATE */}
              {messages.length > 0 && (
                <>
                  {messages.map((message, index) => {
                    const artifactMeta =
                      message.role === "assistant"
                        ? detectArtifact(
                            typeof message.content === "string" ? message.content : message.content.text || "",
                          )
                        : { isArtifact: false }

                    const previousMessage = index > 0 ? messages[index - 1] : undefined
                    const showTimestamp = shouldShowTimestamp(
                      message.timestamp,
                      previousMessage?.timestamp,
                      index === 0 || (previousMessage && previousMessage.role !== message.role),
                    )

                    return (
                      <div key={message.id}>
                        {message.role === "user" && (
                          <div className="mb-6 mt-12 w-full flex justify-end">
                            <div
                              className={cn(
                                "inline-block max-w-[min(600px,_70%)]",
                                "px-4 py-2 rounded-full",
                                "bg-[var(--brain)]/10",
                                "leading-[1.42]",
                              )}
                            >
                              <MessageRenderer
                                content={message.content}
                                messageId={message.id}
                                messageType="user"
                                variant="user-bubble"
                              />
                            </div>
                          </div>
                          // </CHANGE>
                        )}

                        {message.role === "assistant" && (
                          <div className="mb-6 w-full">
                            <div
                              className="flex items-center gap-2 mb-3 text-[12px] leading-[1.4] text-[var(--text-secondary)]/60"
                              title={message.timestamp ? formatAbsoluteTimestamp(message.timestamp) : undefined}
                            >
                              {showTimestamp && message.timestamp && (
                                <span className="font-normal">{formatHumanizedTimestamp(message.timestamp)}</span>
                              )}
                            </div>
                            {/* </CHANGE> */}

                            {artifactMeta.isArtifact ? (
                              <div className="rounded-lg border border-[var(--border)]/20 bg-[var(--background)]/50 px-8 py-6 shadow-sm">
                                <MessageRenderer
                                  content={message.content}
                                  messageId={message.id}
                                  messageType="assistant"
                                  variant="advisor-document"
                                />
                              </div>
                            ) : (
                              <MessageRenderer
                                content={message.content}
                                messageId={message.id}
                                messageType="assistant"
                                variant="advisor-document"
                              />
                            )}
                            {/* </CHANGE> */}
                          </div>
                        )}
                      </div>
                    )
                  })}

                  {isThinking && (
                    <AssistantThinkingRow
                      isExternalOperation={false}
                      onTransitionComplete={() => {
                        console.log("[v0] Thinking visual transition complete")
                      }}
                    />
                  )}
                  {/* </CHANGE> */}
                </>
              )}
            </div>
          </div>
          {/* </CHANGE> */}

          {/* Input at bottom for active chat */}
          {/* P1 Milestone B: Input area refactored */}
          <div className="sticky bottom-0 left-0 right-0 shrink-0 bg-transparent border-t border-[var(--border)]/5 pt-3 pb-3 pb-[env(safe-area-inset-bottom)]">
            <div className="max-w-[950px] mx-auto px-4">
              <div
                className={cn(
                  "relative transition-all duration-200",
                  isMultiLine ? "rounded-[2rem]" : "rounded-full",
                  "bg-slate-900",
                  // Focus state: border + ring only (no fill brightening)
                  "border border-white/10",
                  isInputFocused ? "border-white/20 ring-1 ring-white/10" : "border-white/10",
                  "px-4",
                  isMultiLine ? "py-2.5" : "py-2.5",
                )}
                ref={(el) => {
                  // Regression logging for input dimensions
                  if (el && typeof window !== "undefined") {
                    const styles = window.getComputedStyle(el)
                    console.log("[v0] ACTIVE_INPUT_COMPUTED_STYLES", {
                      backgroundColor: styles.backgroundColor,
                      paddingLeft: styles.paddingLeft,
                      paddingRight: styles.paddingRight,
                      paddingTop: styles.paddingTop,
                      paddingBottom: styles.paddingBottom,
                      borderColor: styles.borderColor,
                      focused: isInputFocused,
                      matchesHome: true,
                    })
                  }
                }}
              >
                <div className="flex items-center gap-2">
                  {/* Left controls: fixed width */}
                  <div className="shrink-0">
                    <button
                      type="button"
                      onClick={() => console.log("Attach clicked")}
                      className="h-10 w-10 rounded-full bg-slate-700/50 flex items-center justify-center hover:bg-slate-700/70 transition-colors"
                      aria-label="Attach file"
                      ref={(el) => {
                        // Log endcap positioning for regression tracking
                        if (el && typeof window !== "undefined") {
                          const rect = el.getBoundingClientRect()
                          const parent = el.parentElement?.parentElement?.parentElement?.getBoundingClientRect()
                          if (parent) {
                            console.log("[v0] ACTIVE_LEFT_ENDCAP_POSITIONING", {
                              buttonWidth: rect.width,
                              buttonHeight: rect.height,
                              leftMargin: rect.left - parent.left,
                              topMargin: rect.top - parent.top,
                              opticalParity: `${rect.left - parent.left}px horizontal ≈ ${rect.top - parent.top}px vertical`,
                            })
                          }
                        }
                      }}
                    >
                      <Plus className="h-5 w-5 text-slate-300" />
                    </button>
                  </div>

                  {/* Center: Textarea wrapper (flex: 1; min-width: 0) */}
                  <div className="flex-1 min-w-0 flex items-center">
                    <textarea
                      ref={textareaRef}
                      value={input}
                      onChange={handleTextareaChange}
                      onKeyDown={handleKeyDown}
                      onFocus={() => setIsInputFocused(true)}
                      onBlur={() => setIsInputFocused(false)}
                      placeholder={
                        messages.length === 0 && !effectiveConversationId
                          ? "What are you working on right now?"
                          : "Type your thoughts..."
                      }
                      rows={1}
                      className={cn(
                        "w-full resize-none bg-transparent text-[18px] leading-[28px] text-white placeholder-slate-400",
                        "border-0 outline-none focus:ring-0",
                        "whitespace-pre-wrap",
                        "[overflow-wrap:anywhere]",
                        "[word-break:break-word]",
                      )}
                      style={{
                        minHeight: "24px",
                        padding: "0",
                        height: computedHeight,
                        maxHeight: "200px",
                        overflow: computedHeight === "200px" ? "auto" : "hidden",
                      }}
                    />
                  </div>

                  {/* Right controls: fixed width */}
                  <div className="shrink-0">
                    <button
                      type="button"
                      onClick={async () => await handleSubmit()}
                      disabled={!input.trim() || isLoading || isSending}
                      className={cn(
                        "h-10 w-10 rounded-full",
                        "flex items-center justify-center transition-all duration-200",
                        input.trim() && !isLoading && !isSending
                          ? "bg-[var(--brain)] hover:bg-[var(--brain-hover)] cursor-pointer"
                          : "bg-[var(--brain)]/30 cursor-not-allowed opacity-60",
                      )}
                      aria-label="Send message"
                      ref={(el) => {
                        // Log endcap positioning for regression tracking
                        if (el && typeof window !== "undefined") {
                          const rect = el.getBoundingClientRect()
                          const parent = el.parentElement?.parentElement?.parentElement?.getBoundingClientRect()
                          if (parent) {
                            console.log("[v0] ACTIVE_RIGHT_ENDCAP_POSITIONING", {
                              buttonWidth: rect.width,
                              buttonHeight: rect.height,
                              rightMargin: parent.right - rect.right,
                              topMargin: rect.top - parent.top,
                              opticalParity: `${parent.right - rect.right}px horizontal ≈ ${rect.top - parent.top}px vertical`,
                            })
                          }
                        }
                      }}
                    >
                      {isLoading || isSending ? (
                        <Loader2 className="h-5 w-5 text-white animate-spin" />
                      ) : (
                        <ArrowUp className="h-5 w-5 text-white" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              {/* </CHANGE> */}

              {messages.length > 0 && (
                <p className="text-[12px] leading-[18px] text-center text-slate-400/70 mt-3">
                  AI-generated guidance may contain errors. Use your judgment before acting.
                </p>
              )}
            </div>
          </div>
          {/* </CHANGE> */}

          {chatModeStoreMode === "profile" && !FF_ALWAYS_ADVISOR_MODE && (
            <button
              onClick={handleSkipToAdvisor}
              className="block mx-auto text-xs text-muted-foreground hover:text-foreground transition-colors mt-2"
            >
              Skip profile and ask a question instead
            </button>
          )}
        </div>
      )}

      {quickWins.length > 0 && <QuickWinsToast wins={quickWins} onDismiss={() => setQuickWins([])} />}
      {isFirstImpressions && (
        <FirstImpressionsCard
          savedFields={savedImpressions}
          onClose={() => setIsFirstImpressions(false)}
          onViewBrain={onSignupClick}
        />
      )}
      {/* </CHANGE> */}
    </div>
  )
}

// Helper component to render memory saved confirmations
function MemorySavedConfirmation({ savedMemories }: { savedMemories: Message["savedMemories"] }) {
  if (!savedMemories || savedMemories.length === 0) return null

  return (
    <div className="flex items-start gap-3">
      <div className="mt-1 p-2 rounded-full bg-green-500/10">
        <CheckCircle className="h-4 w-4 text-green-500" />
      </div>
      <div className="flex-1 p-3 px-4 rounded-2xl bg-[var(--surface-alt)] border border-[var(--border-subtle)]">
        <p className="text-sm text-foreground">
          Memory saved:
          <span className="font-medium"> {savedMemories.map((m) => m.label).join(", ")}</span>
        </p>
      </div>
    </div>
  )
}
